import * as Api from '@ViewModels';
import * as React from 'react';
import { useStreamingResponseMutation } from '../../../hooks/useStreamingResponseMutation';
import { useErrorMessages, useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { useAIAssistantCreateConversationMutation } from '../../../queries/AI/useAIAssistantCreateConversationMutation';
import { getConversationChatMessageQueryKey } from '../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { reactQueryClient } from '../../../web/ReactQueryProvider';
import { useLeadAIContext } from '../../queries/LeadAIContext';
import {
	AICoachingMessage,
	TysonCoachingContextData,
	TYSON_ASSISTANT_NAME,
	TYSON_COACHING_FALLBACK_PROMPT,
	TYSON_END_OF_CALL_SUMMARY_PROMPT,
} from '../types/coaching';
import { TranscriptEntry } from '../types';

interface UseTysonRealtimeCoachingReturn {
	coachingMessages: AICoachingMessage[];
	conversationId: string | null;
	isCreatingConversation: boolean;
	isAIResponding: boolean;
	createConversation: (data?: TysonCoachingContextData) => Promise<void>;
	sendTranscriptEntry: (entry: TranscriptEntry) => void;
	sendEndOfCallSummary: () => void;
	resetCoaching: () => void;
}

export const useTysonRealtimeCoaching = (contextData: TysonCoachingContextData): UseTysonRealtimeCoachingReturn => {
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('useTysonRealtimeCoaching');
	const userSession = useUserSession();

	const [conversationId, setConversationId] = React.useState<string>(null);
	const conversationIdRef = React.useRef<string>(null);
	const [coachingMessages, setCoachingMessages] = React.useState<AICoachingMessage[]>([]);
	const [pendingTranscriptEntries, setPendingTranscriptEntries] = React.useState<TranscriptEntry[]>([]);
	const [lastAIResponseTime, setLastAIResponseTime] = React.useState<number>(Date.now());

	// Keep ref in sync with state
	React.useEffect(() => {
		conversationIdRef.current = conversationId;
	}, [conversationId]);

	// Get cached lead AI context
	const { data: leadAIContextData } = useLeadAIContext(contextData?.companyId, contextData?.leadServedId);

	// Create AI conversation
	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('aiAssistantCreateConversationMutation', error);
		},
		onSuccess: conversation => {
			setConversationId(conversation.id);

			// Send initial context to AI
			const hubspotData = leadAIContextData?.data;
			const chatMessages: Api.AIChatMessage[] = [];

			// System message with coaching instructions (use objectivePrompt from backend, fallback to default)
			const coachingSystemPrompt = contextData?.objectivePrompt || TYSON_COACHING_FALLBACK_PROMPT;
			chatMessages.push({
				role: Api.AIChatRole.System,
				text: coachingSystemPrompt,
				id: null,
			});

			// Include HubSpot data if available
			if (hubspotData) {
				const cleanedHubSpotData = hubspotData.replace(/\\n/g, '\n').replace(/\\r/g, '\r');
				chatMessages.push({
					role: Api.AIChatRole.System,
					text: `# Lead AI Context\n\n${cleanedHubSpotData}`,
					id: null,
				});
			}

			// Initial user message to start coaching
			chatMessages.push({
				role: Api.AIChatRole.User,
				text: 'I am about to start a practice sales call. Please provide an initial message to let me know you are ready to coach me through this call.',
				id: null,
			});

			// Start streaming the initial AI response
			aiPromptMutation.mutate({
				assistantName: TYSON_ASSISTANT_NAME,
				convoId: conversation.id,
				chatRequest: { chatMessages },
			});
		},
	});

	// Stream AI responses
	const aiPromptMutation = useStreamingResponseMutation({
		mutationFn: ({
			assistantName,
			convoId,
			chatRequest,
		}: {
			assistantName: string;
			convoId: string;
			chatRequest: Api.AIMessageRequest;
		}) =>
			new Promise<Response>((resolve, reject) => {
				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${assistantName}/conversation/${convoId}/stream`,
					}),
					'POST',
					chatRequest,
					resolve,
					reject
				);
			}),
		onEvent: (event, params) => {
			const data = JSON.parse(event.data);
			if (event.event !== 'Response') {
				return;
			}

			const messageText = data?.message?.text;
			const isDelta = data?.isDelta;

			if (messageText) {
				setCoachingMessages(prev => {
					const lastMessage = prev[prev.length - 1];

					if (lastMessage && lastMessage.isStreaming) {
						// Update existing streaming message - just concatenate directly
						// LLM tokens include proper spacing
						const updatedText = isDelta ? lastMessage.text + messageText : messageText;

						return [
							...prev.slice(0, -1),
							{
								...lastMessage,
								text: updatedText,
							},
						];
					} else {
						// Create new message
						return [
							...prev,
							{
								id: data.Value?.Id || `msg-${Date.now()}`,
								text: messageText,
								timestamp: new Date(),
								isStreaming: true,
							},
						];
					}
				});

				// Update query cache
				reactQueryClient.setQueryData(
					getConversationChatMessageQueryKey({ id: params.convoId }),
					(messages: Api.AIChatMessage[]) => {
						if (messages?.length > 0 && messages[messages.length - 1].role === Api.AIChatRole.Assistant) {
							const lastAssistantMessage = messages.pop();
							// LLM tokens include proper spacing - just concatenate directly
							const updatedText = isDelta ? lastAssistantMessage.text + messageText : messageText;
							return [...messages, { ...lastAssistantMessage, text: updatedText }];
						}
						return [...(messages || []), { id: data.Value?.Id, text: messageText, role: Api.AIChatRole.Assistant }];
					}
				);
			}
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('aiPrompt', error as Api.IOperationResultNoValue);
		},
		onSuccess: () => {
			// Mark last message as complete
			setCoachingMessages(prev => {
				if (prev.length === 0) return prev;
				const lastMessage = prev[prev.length - 1];
				return [...prev.slice(0, -1), { ...lastMessage, isStreaming: false }];
			});

			// Update last response time
			setLastAIResponseTime(Date.now());

			// Send any pending transcript entries
			if (pendingTranscriptEntries.length > 0) {
				const entriesToSend = [...pendingTranscriptEntries];
				setPendingTranscriptEntries([]);
				sendTranscriptBatch(entriesToSend);
			}
		},
	});

	// Send transcript batch to AI
	const sendTranscriptBatch = React.useCallback(
		(entries: TranscriptEntry[]) => {
			const currentConversationId = conversationIdRef.current;
			if (!currentConversationId || entries.length === 0) {
				return;
			}

			const formattedTranscript = entries
				.map(entry => `[${entry.timestamp.toLocaleTimeString()}] ${entry.speaker}: ${entry.message}`)
				.join('\n');

			aiPromptMutation.mutate({
				assistantName: TYSON_ASSISTANT_NAME,
				convoId: currentConversationId,
				chatRequest: {
					chatMessages: [
						{
							role: Api.AIChatRole.User,
							text: `New transcript:\n${formattedTranscript}`,
							id: null,
						},
					],
				},
			});
		},
		[aiPromptMutation]
	);

	// Send single transcript entry with throttling
	const sendTranscriptEntry = React.useCallback(
		(entry: TranscriptEntry) => {
			if (!conversationIdRef.current) {
				return;
			}
			setPendingTranscriptEntries(prev => [...prev, entry]);
		},
		[] // No dependencies - always use latest ref value
	);

	// Periodically check and send pending transcript entries
	React.useEffect(() => {
		if (!conversationId) return;

		const interval = setInterval(() => {
			const timeSinceLastResponse = Date.now() - lastAIResponseTime;
			const bufferTime = 3000; // 3 seconds

			if (pendingTranscriptEntries.length > 0 && !aiPromptMutation.isStreaming && timeSinceLastResponse > bufferTime) {
				const entriesToSend = [...pendingTranscriptEntries];
				setPendingTranscriptEntries([]);
				sendTranscriptBatch(entriesToSend);
			}
		}, 2000); // Check every 2 seconds

		return () => clearInterval(interval);
	}, [conversationId, pendingTranscriptEntries, lastAIResponseTime, aiPromptMutation.isStreaming, sendTranscriptBatch]);

	// Create conversation
	const createConversation = React.useCallback(
		async (data?: TysonCoachingContextData) => {
			if (aiAssistantCreateConversationMutation.isPending) return;

			// Use provided data or fall back to contextData
			const ctx = data || contextData;

			// Validate context data
			if (!ctx) {
				throw new Error('Cannot create conversation: contextData is null');
			}

			if (!ctx.companyId) {
				throw new Error('Cannot create conversation: companyId is required');
			}

			if (!ctx.contactId) {
				throw new Error('Cannot create conversation: contactId is required');
			}

			// Lead AI context is automatically fetched and cached by useLeadAIContext query
			// when companyId is available - no manual prefetch needed

			// Create conversation
			await aiAssistantCreateConversationMutation.mutateAsync({
				assistantName: TYSON_ASSISTANT_NAME,
				phoneCallId: ctx.phoneCallId,
				companyId: ctx.companyId,
				leadServedId: ctx.leadServedId,
			});
		},
		[contextData, aiAssistantCreateConversationMutation]
	);

	// Send end-of-call summary request
	const sendEndOfCallSummary = React.useCallback(() => {
		const currentConversationId = conversationIdRef.current;
		if (!currentConversationId) {
			return;
		}

		aiPromptMutation.mutate({
			assistantName: TYSON_ASSISTANT_NAME,
			convoId: currentConversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text: TYSON_END_OF_CALL_SUMMARY_PROMPT,
						id: null,
					},
				],
			},
		});
	}, [aiPromptMutation]);

	// Reset coaching state
	const resetCoaching = React.useCallback(() => {
		setConversationId(null);
		conversationIdRef.current = null;
		setCoachingMessages([]);
		setPendingTranscriptEntries([]);
		setLastAIResponseTime(Date.now());
	}, []);

	return {
		coachingMessages,
		conversationId,
		isCreatingConversation: aiAssistantCreateConversationMutation.isPending,
		isAIResponding: aiPromptMutation.isStreaming || aiPromptMutation.isLoading,
		createConversation,
		sendTranscriptEntry,
		sendEndOfCallSummary,
		resetCoaching,
	};
};
